<!--
  Popover for moving a live stream to another window or screen. The window part is
  WindowGrid in a box; whole screens sit under it.
-->
<script lang="ts">
  import WindowGrid from './WindowGrid.svelte'
  import { listMonitors, type MonitorInfo, type WindowInfo } from './tauri'
  import { t } from './i18n.svelte'

  export type SwitchTarget =
    { kind: 'window'; window: WindowInfo } | { kind: 'screen'; monitor: MonitorInfo }

  let {
    currentHwnd,
    currentMonitor = null,
    switching = false,
    label,
    onSwitch,
  }: {
    currentHwnd: number | null
    currentMonitor?: number | null
    switching?: boolean
    /** Name of what is being shared now. */
    label: string
    onSwitch: (target: SwitchTarget) => void
  } = $props()

  let open = $state(false)
  let windows = $state<WindowInfo[]>([])
  let thumbs = $state<Record<number, string>>({})
  let monitors = $state<MonitorInfo[]>([])

  $effect(() => {
    if (!open) return
    void listMonitors()
      .then((found) => (monitors = found))
      .catch(() => {})
  })
</script>

<div class="switch">
  <button
    type="button"
    class="switch__btn"
    disabled={switching}
    aria-busy={switching}
    onclick={() => (open = !open)}
    aria-expanded={open}
  >
    ⇄ {t('Switch window or screen')} <small>({label})</small>
  </button>
  {#if open}
    <button type="button" class="scrim" aria-label={t('Close')} onclick={() => (open = false)}
    ></button>
    <div class="switch__pop" role="dialog" aria-label={t('Switch to')}>
      <div class="spread" style="margin-bottom: 4px">
        <span class="eyebrow" style="padding: 0">{t('Switch to')}</span>
        <span class="faint" style="font-size: 12px">{t('Friends keep watching')}</span>
      </div>
      <WindowGrid
        active={open}
        selectedHwnd={currentMonitor === null ? currentHwnd : null}
        bind:windows
        bind:thumbs
        onPick={(window) => {
          open = false
          onSwitch({ kind: 'window', window })
        }}
      />
      {#if monitors.length}
        <div class="switch__screens">
          {#each monitors as monitor (monitor.index)}
            <button
              type="button"
              class="switch__screen"
              aria-pressed={currentMonitor === monitor.index}
              onclick={() => {
                open = false
                onSwitch({ kind: 'screen', monitor })
              }}
            >
              <span>{t('Screen')}</span>
              <strong>{monitor.name}</strong>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .switch {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
  }
  .switch__btn {
    height: 38px;
    padding: 0 16px;
    border-radius: 12px;
    background: var(--text);
    border-color: var(--text);
    color: var(--on-ink);
    font-size: 13px;
    font-weight: 800;
    max-width: min(420px, 100%);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .switch__btn small {
    font-weight: 600;
    color: var(--accent);
  }
  .switch__pop {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 40;
    width: 520px;
    max-height: 70vh;
    overflow-y: auto;
    padding: 16px;
    border-radius: 18px;
    background: var(--bg-raised);
    border: 1px solid var(--border);
    box-shadow: 0 24px 48px -16px rgba(42, 23, 32, 0.45);
  }
  .switch__pop :global(.picker__head) {
    padding-top: 6px !important;
  }
  .switch__pop :global(.picker__grid) {
    padding-bottom: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding-top: 8px;
  }
  .switch__screens {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }
  .switch__screen {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    height: auto;
    padding: 8px 14px;
    border: 1.5px dashed var(--border-strong);
    border-radius: 10px;
    background: transparent;
    font-size: 13px;
  }
  .switch__screen span {
    font-size: 11px;
    color: var(--text-dim);
  }
  .switch__screen[aria-pressed='true'] {
    border-style: solid;
    border-color: var(--accent);
  }
</style>
